import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { useServerSearch } from '../TableFilterContext'
import { SEARCHABLE } from '../searchable'
import type { CappedList } from '../api/client'
import { ApiError } from '../api/client'
import {
  PROCEDURE_LEADS_KEY,
  fetchProcedureLeads,
  updateProcedureLead,
} from '../api/procedureRequests'
import {
  SPECIALIST_LEADS_KEY,
  fetchSpecialistLeads,
  updateSpecialistLead,
} from '../api/specialistRequests'
import { EmptyState } from '../components/EmptyState'
import { ListDisclosure } from '../components/ListDisclosure'
import { RecordCard, type Fact } from '../components/RecordCard'
import { compactAge } from '../api/dashboard'

/** A lead, normalised across the two sources for the shared table. */
type Lead = {
  reference: string
  description: string
  details?: string | null
  area: string | null
  patient_name: string | null
  patient_phone: string | null
  status: string
  concierge_notes: string | null
  created_at: string
}

type Segment = 'procedures' | 'specialists'

/** What each segment needs from the page: the status filter and the one box. */
type SegmentProps = {
  status: string | null
  term: string
  setResultCount: (n: number | null) => void
}

const STATUS_OPTIONS = ['new', 'in_progress', 'responded', 'closed'] as const
const STATUS_FILTERS = [
  { key: null, label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'responded', label: 'Responded' },
  { key: 'closed', label: 'Closed' },
] as const

// M17-T14: the amber pill takes text-ink-1, not text-bed-warn. --bed-warn on
// --bed-warn-bg measures 3.76:1, below AA for pill text at this size. The amber
// stays as the status signal on the background; only the label goes dark. There
// is no dark step on the warning ramp to reach for — audit G35, Figma publishes
// no consumable warning ramp — so when the designer supplies one it lands here.

/**
 * Concierge Leads queue (M14-T13). Two lead types — "my procedure isn't listed"
 * (M12-T04) and "my specialist isn't listed" (M14-T09) — in one page, switched by
 * a segmented control, because it's the same concierge job worked by one person.
 * The tables/queries stay separate underneath; only the shell is shared.
 * amtc_admin only (both endpoints are). Also closes the M12 gap where the
 * procedure leads had no UI at all.
 */
export function LeadsPage(): ReactNode {
  const [segment, setSegment] = useState<Segment>('procedures')
  const [status, setStatus] = useState<string | null>(null)

  // The header box searches the whole queue rather than the cards on screen
  // (M25-T01). Both lead queues are CAPPED windows — `LIMIT $1` with no expiry
  // — so filtering only what is loaded would leave an admin unable to tell
  // "that patient never enquired" from "that patient is row 62 of 214", which
  // is the harm M23 gap A5 existed to remove.
  //
  // The term lives here, above the segment switch, because ONE box sits above
  // BOTH segments. `setResultCount` is passed down and only the mounted
  // segment ever calls it.
  const { term, setResultCount } = useServerSearch(SEARCHABLE.leads)

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">Leads</h1>
        <p className="mt-1 text-sm text-ink-3">
          Patients who couldn&apos;t find what they needed. Call them back and mark
          the lead.
        </p>
      </div>

      <div role="tablist" aria-label="Lead type" className="flex w-fit rounded-full border border-line-2 bg-bg-app p-0.5">
        {(
          [
            ['procedures', 'Procedures'],
            ['specialists', 'Specialists'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={segment === key}
            onClick={() => setSegment(key)}
            className={`rounded-full px-4 py-1 text-sm font-medium ${
              segment === key
                ? 'bg-accent text-white'
                : 'text-ink-2 hover:text-ink-1'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div role="group" aria-label="Filter by status" className="flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            aria-pressed={status === f.key}
            onClick={() => setStatus(f.key)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              status === f.key
                ? 'border-accent bg-accent text-white'
                : 'border-line-2 bg-bg-app text-ink-1 hover:bg-bg-soft'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {segment === 'procedures' ? (
        <ProcedureLeads status={status} term={term} setResultCount={setResultCount} />
      ) : (
        <SpecialistLeads status={status} term={term} setResultCount={setResultCount} />
      )}
    </section>
  )
}

function ProcedureLeads({
  status,
  term,
  setResultCount,
}: SegmentProps): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: [...PROCEDURE_LEADS_KEY, status, term],
    queryFn: () => fetchProcedureLeads(token, status, term),
    enabled: Boolean(token),
  })
  const rows = query.data?.data
  useEffect(() => {
    setResultCount(rows === undefined ? null : rows.length)
  }, [rows, setResultCount])
  return (
    <LeadTable
      query={query}
      onSave={(reference, input) => updateProcedureLead(token, reference, input)}
      invalidateKey={PROCEDURE_LEADS_KEY}
      queryClient={queryClient}
    />
  )
}

function SpecialistLeads({
  status,
  term,
  setResultCount,
}: SegmentProps): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: [...SPECIALIST_LEADS_KEY, status, term],
    queryFn: () => fetchSpecialistLeads(token, status, term),
    enabled: Boolean(token),
  })
  const rows = query.data?.data
  useEffect(() => {
    setResultCount(rows === undefined ? null : rows.length)
  }, [rows, setResultCount])
  return (
    <LeadTable
      query={query}
      onSave={(reference, input) => updateSpecialistLead(token, reference, input)}
      invalidateKey={SPECIALIST_LEADS_KEY}
      queryClient={queryClient}
    />
  )
}

type LeadQuery = {
  data: CappedList<Lead> | undefined
  isPending: boolean
  isError: boolean
  error: unknown
  refetch: () => void
}

function LeadTable({
  query,
  onSave,
  invalidateKey,
  queryClient,
}: {
  query: LeadQuery
  onSave: (
    reference: string,
    input: { status: string; concierge_notes?: string },
  ) => Promise<void>
  invalidateKey: readonly string[]
  queryClient: ReturnType<typeof useQueryClient>
}): ReactNode {
  if (query.isPending) {
    return (
      <div
        role="status"
        aria-label="Loading leads"
        className="h-40 w-full max-w-3xl animate-pulse rounded-2xl bg-bg-sunken"
      />
    )
  }
  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-4 rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
      >
        <p className="text-sm font-medium text-bed-bad">
          {query.error instanceof ApiError ? query.error.message : 'Could not load leads.'}
        </p>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="inline-flex h-8 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
        >
          Retry
        </button>
      </div>
    )
  }
  const rows = query.data?.data ?? []
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-line-1">
        {/* An empty Leads page is the GOOD outcome — every search found
            something. Saying "No results" would read as a failure. */}
        <EmptyState title="No leads.">
          Nobody has searched and come up empty. When someone does, they land
          here so the team can call them back.
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Both lead queues are capped at the newest 50. One disclosure here
          rather than one per segment: the two segments share this renderer, so
          they cannot disagree about whether they are showing a window. */}
      <ListDisclosure shown={rows.length} total={query.data?.total} />
      {rows.map((lead) => (
        <LeadRow
          key={lead.reference}
          lead={lead}
          onSave={onSave}
          onSaved={() =>
            void queryClient.invalidateQueries({ queryKey: invalidateKey })
          }
        />
      ))}
    </div>
  )
}

function LeadRow({
  lead,
  onSave,
  onSaved,
}: {
  lead: Lead
  onSave: (
    reference: string,
    input: { status: string; concierge_notes?: string },
  ) => Promise<void>
  onSaved: () => void
}): ReactNode {
  const [status, setStatus] = useState(lead.status)
  const [notes, setNotes] = useState(lead.concierge_notes ?? '')

  const mutation = useMutation({
    mutationFn: () => onSave(lead.reference, { status, concierge_notes: notes }),
    onSuccess: onSaved,
  })

  // The frame draws LOOKING FOR beneath a title that already IS what they are
  // looking for — "Pediatric Doctor" twice on the same card. The title keeps
  // it; the fact is dropped rather than duplicated. RecordCard takes a
  // variable number of facts precisely so this is not awkward.
  const facts: Fact[] = [
    ...(lead.area ? [{ label: 'AREA', value: lead.area }] : []),
    {
      label: 'PATIENT',
      value: (
        <>
          {lead.patient_name ?? '—'}
          {lead.patient_phone ? (
            <>
              {' · '}
              <a
                href={`tel:${lead.patient_phone}`}
                className="font-medium text-accent-ink hover:underline"
              >
                {lead.patient_phone}
              </a>
            </>
          ) : null}
        </>
      ),
    },
  ]

  return (
    <RecordCard
      title={lead.description}
      reference={lead.reference}
      age={compactAge(lead.created_at)}
      status={lead.status}
      quote={lead.details ?? null}
      facts={facts}
      actions={
        <>
          {/* Dials THIS lead's number. The frame prints the same
              02431245678 on every card, and M22-T13 already had to fix call
              buttons that dialled a placeholder. */}
          {lead.patient_phone ? (
            <a
              href={`tel:${lead.patient_phone}`}
              className="inline-flex h-9 items-center justify-center rounded-[8px] bg-accent px-3.5 text-sm font-semibold text-accent-on hover:opacity-90"
            >
              Call {lead.patient_phone}
            </a>
          ) : (
            <span className="inline-flex h-9 cursor-not-allowed items-center rounded-[8px] border border-line-2 px-3.5 text-sm text-ink-3">
              No number given
            </span>
          )}

          <label className="sr-only" htmlFor={`status-${lead.reference}`}>
            Status for {lead.reference}
          </label>
          <select
            id={`status-${lead.reference}`}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-[8px] border border-line-2 bg-bg-app px-2 text-sm capitalize"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>

          <label className="sr-only" htmlFor={`notes-${lead.reference}`}>
            Concierge notes for {lead.reference}
          </label>
          <input
            id={`notes-${lead.reference}`}
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes from call"
            className="h-9 min-w-56 flex-1 rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm"
          />

          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            className="inline-flex h-9 items-center rounded-[8px] border border-line-2 bg-bg-app px-3.5 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>

          {mutation.isError ? (
            <p role="alert" className="w-full text-xs text-bed-bad">
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : 'Save failed.'}
            </p>
          ) : null}
        </>
      }
    />
  )
}
